import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Swipeable } from 'react-swipeable';
import { useMediaQuery } from 'react-responsive';
import TimelineCarousel from '../TimelineCarousel/TimelineCarousel';
import { setPBPBlockScroll } from '../../../store/reducers/mainReducer';
import { useSwipeEvent } from '../../../models/Hooks';
import './Timeline.scss';

const Timeline = ({ isActive }) => {
  const dispatch = useDispatch();
  const [activeYear, setActiveYear] = useState(0);
  const [autoScroll, setAutoScroll] = useState(false);
  const [lightboxYear, setLightboxYear] = useState(null);
  const [swipeEvent, setSwipeEvent] = useState(false);
  const [cardActive, setCardActive] = useState(false);
  const cmsData = useSelector(state => state.data.cms.poweredByPurpose);
  const cardsRef = useRef(null);
  const yearsRef = useRef(null);
  const timelineRef = useRef(null);
  const isPortrait = useMediaQuery({ query: '(orientation: portrait)' });
  const scrollTime = 300;
  const timeStep = 30;
  let timelineData =
    (cmsData && cmsData.NetworkMilestones && cmsData.NetworkMilestones.Timeline) || [];

  if (timelineData.length) {
    timelineData = timelineData.sort((a, b) => b.Year - a.Year);
  }

  useEffect(() => {
    if (isActive) {
      dispatch(setPBPBlockScroll(true));
      setCardActive(false);
    }
  }, [isActive]);

  useEffect(() => {
    dispatch(setPBPBlockScroll(lightboxYear >= 0));
    setCardActive(lightboxYear);
  }, [lightboxYear]);

  // years scroll handler
  useLayoutEffect(() => {
    const ref = cardsRef.current;
    let scrollIntoViewTimer = null;
    const handleScroll = e => {
      e.stopPropagation();
      if (
        (ref.scrollTop <= 0 || Math.round(ref.scrollTop) >= ref.scrollHeight - ref.clientHeight) &&
        !cardActive
      ) {
        setTimeout(() => dispatch(setPBPBlockScroll(false)), 500);
      } else {
        dispatch(setPBPBlockScroll(true));
      }
      if (autoScroll || cardActive) {
        return;
      }
      let i;
      for (i = 0; i < ref.children.length; i++) {
        if (
          ref.scrollTop + ref.offsetTop <=
          ref.children[i].offsetHeight + ref.children[i].offsetTop - 300
        ) {
          break;
        }
      }
      if (i >= ref.children.length) {
        i = ref.children.length - 1;
      }
      setActiveYear(i);
      if (scrollIntoViewTimer) {
        clearTimeout(scrollIntoViewTimer);
      }
      scrollIntoViewTimer = setTimeout(() => {
        if (yearsRef.current) {
          scrollIntoView(yearsRef.current.children[i]);
        }
      }, 100);
    };

    ref.addEventListener('scroll', handleScroll);
    return () => {
      ref.removeEventListener('scroll', handleScroll);
      dispatch(setPBPBlockScroll(false));
    };
  }, [autoScroll]);

  const onSwipe = e => {
    if (lightboxYear) {
      if (e.dir !== 'Right' && e.dir !== 'Left') {
        setLightboxYear(null);
      }
    } else {
      setSwipeEvent(true);
      setTimeout(() => {
        setSwipeEvent(false);
      }, 100);
    }
  };

  const onExitLightbox = () => {
    if (lightboxYear) {
      setLightboxYear(null);
      setTimeout(() => {
        dispatch(setPBPBlockScroll(true));
      }, 500);
    }
  };

  useSwipeEvent(window, e => {
    if (!lightboxYear || !e) {
      return;
    }
    if (e.dir === 'Up' || e.dir === 'Down') {
      setLightboxYear(null);
    }
  });

  useLayoutEffect(() => {
    onExitLightbox();

    window.addEventListener('resize', onExitLightbox);

    return () => {
      window.removeEventListener('resize', onExitLightbox);
    };
  }, []);

  const onCardClick = (y, e) => {
    if (!isPortrait) {
      return;
    }
    const elType = e.nativeEvent && e.nativeEvent.target.type;
    let classList = e.nativeEvent && e.nativeEvent.target && e.nativeEvent.target.classList;
    if (!classList || !classList.length || !classList.contains) {
      classList = [];
    }
    if (
      elType === 'button' ||
      (classList.contains &&
        (classList.contains('btn-next-container') || classList.contains('carousel__dot-group')))
    ) {
      return;
    }
    if (!lightboxYear && !swipeEvent) {
      setLightboxYear(y);
      dispatch(setPBPBlockScroll(true));
    }
  };

  const onYearClick = i => {
    if (!autoScroll) {
      setActiveYear(i);
      scrollIntoView(cardsRef.current.children[i], true);
    }
  };

  const scrollIntoView = (element, updateState = false) => {
    let target = element.offsetTop - Math.round(element.parentElement.offsetTop);
    if (target > element.parentElement.scrollHeight - element.parentElement.offsetHeight) {
      target = element.parentElement.scrollHeight - element.parentElement.offsetHeight;
    }
    const step = (target - element.parentElement.scrollTop) / (scrollTime / timeStep);
    if (step === 0) {
      return;
    }
    updateState && setAutoScroll(true);
    let counter = 0;

    const timerId = setInterval(() => {
      element.parentElement.scrollTop += step;
      counter++;
      if (counter * timeStep >= scrollTime) {
        element.parentElement.scrollTop = target;
        updateState && setAutoScroll(false);
        clearInterval(timerId);
      }
    }, timeStep);
  };

  const handleTimelineExitClicked = () => {
    onExitLightbox();
  };

  const handleExploreClicked = (year) => {
    console.log('explore - lightbox hook pressed');
    onCardClick(year, 
      {
        nativeEvent: null
      }
    );
  };

  return (
    <div className="timeline" ref={timelineRef}>
      <div className="width-wrapper">
        <ul className="years" ref={yearsRef}>
          {timelineData.map((y, i) => (
            <li
              key={y.Key}
              onClick={() => onYearClick(i)} role="button" tabIndex={i * 100}
              onKeyPress={() => onYearClick(i)}
              className={`${activeYear === i ? 'active' : ''} ${
                isPortrait ? 'portrait' : 'landscape'
              }`}
            >
              <span>{y.Year}</span>
            </li>
          ))}
        </ul>
        <div className="cards" ref={cardsRef}>
          {timelineData.map((y, i) => (
            <>
            <div
              className={`card ${lightboxYear === y.Year ? 'lightbox' : ''}`}
              key={'#' + y.Year}
              onClick={e => onCardClick(y.Year, e)}
            >
              <Swipeable
                trackTouch={true}
                trackMouse={true}
                onSwipedDown={onExitLightbox}
                onSwipedUp={onExitLightbox}
                onSwiped={onSwipe}
                onClick
                className="container-block"
              >
                <TimelineCarousel
                  card={y}
                  isActive={isActive && activeYear === i}
                  inLightbox={lightboxYear === y.Year}
                  onExploreClicked={handleExploreClicked}
                  onExitClicked={handleTimelineExitClicked}
                />
              </Swipeable>
            </div>
            {lightboxYear === y.Year && <div className={`faux-card lightbox`}/>}
            </>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Timeline;
